import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Input } from "antd";
import { ArrowUpOutlined, ReloadOutlined } from "@ant-design/icons";
import AuthService from "../../base/services/authentication.service";
import { ASK_ERROR_FALLBACK, askErrorMessage, askHelpAssistant } from "./helpCenterApi";
import {
   answerIdOf,
   clearChat,
   historyBefore,
   nextMessageId,
   readChat,
   settleAnswer,
   settleError,
   startQuestion,
   startRetry,
   toHistory,
   writeChat
} from "./helpChat";
import { ASK_SUGGESTIONS } from "./helpCenterConfig";
import { renderAnswer, unlinkMissingArticles } from "./guideHelpers";
import { ArticleLink, useGuideLinks } from "./UserGuide";
import SparkleIcon from "../../pages/workspace/components/survey/components/aiChat/SparkleIcon";

const MAX_QUESTION_LENGTH = 500;

// Only the suggestions whose article this user can see, so a data collector
// isn't offered questions about building surveys.
const getSuggestions = (articles, count = 4) =>
   ASK_SUGGESTIONS.filter((item) => articles.some((article) => article.id === item.article)).slice(
      0,
      count
   );

/**
 * The Ask AI conversation. It lives in HelpCenter, so it survives the drawer
 * closing and the user moving to another page, and in this tab's
 * sessionStorage (helpChat.js), so it also survives a reload.
 */
export const useHelpAssistant = () => {
   const [messages, setMessages] = useState(readChat);
   const [isPending, setIsPending] = useState(false);
   // Unique within the chat; a restored one keeps the ids it was saved with.
   const nextId = useRef(nextMessageId(messages));
   // Bumped by "New chat", so an answer to the old chat that arrives late is dropped.
   const chat = useRef(0);
   const organizationId = AuthService.getOrganizationId();
   const askedIn = useRef(organizationId);

   useEffect(() => {
      writeChat(messages);
   }, [messages]);

   const ask = useCallback(
      async (question, { retryId } = {}) => {
         const text = question.trim();
         if (!text || isPending) return;

         const thisChat = chat.current;
         const isRetry = retryId !== undefined;
         const questionId = isRetry ? retryId : nextId.current++;
         const answerId = isRetry ? answerIdOf(messages, retryId) : nextId.current++;
         // A retry is the same turn asked again, so it is told what the
         // question was told the first time, not about its own failure.
         const history = isRetry ? historyBefore(messages, retryId) : toHistory(messages);

         setMessages((list) =>
            isRetry
               ? startRetry(list, { questionId, answerId })
               : startQuestion(list, { questionId, answerId, content: text })
         );
         setIsPending(true);

         let settle;
         try {
            const { answer, sources } = await askHelpAssistant({
               question: text,
               history,
               page: window.location.pathname
            });
            settle = (list) => settleAnswer(list, { questionId, answerId, answer, sources });
         } catch (error) {
            settle = (list) =>
               settleError(list, { questionId, answerId, error: askErrorMessage(error) });
         }

         if (chat.current !== thisChat) return;
         setMessages(settle);
         setIsPending(false);
      },
      [isPending, messages]
   );

   const reset = useCallback(() => {
      chat.current += 1;
      nextId.current = 1;
      setMessages([]);
      setIsPending(false);
      clearChat();
   }, []);

   // Roles and the surveys on offer change with the organization, so an answer
   // given in the old one is not kept around to be followed up in the new.
   useEffect(() => {
      if (askedIn.current === organizationId) return;
      askedIn.current = organizationId;
      reset();
   }, [organizationId, reset]);

   return { messages, isPending, ask, reset };
};

const AssistantMessage = ({ message, articles, onLinkClick, onOpenArticle, onRetry, footer }) => {
   const html = useMemo(
      () => renderAnswer(unlinkMissingArticles(message.content, articles)),
      [message.content, articles]
   );
   const sources = (message.sources || [])
      .map((id) => articles.find((article) => article.id === id))
      .filter(Boolean);

   if (message.status === "failed") {
      return (
         <div className="help-ask__message help-ask__message--assistant help-ask__message--error">
            <p>{message.content || ASK_ERROR_FALLBACK}</p>
            <Button size="small" icon={<ReloadOutlined />} onClick={onRetry}>
               Try again
            </Button>
         </div>
      );
   }

   return (
      <div className="help-ask__message help-ask__message--assistant">
         {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
         <div
            className="help-ask__answer"
            onClick={onLinkClick}
            dangerouslySetInnerHTML={{ __html: html }}
         />
         {sources.length > 0 && (
            <div className="help-ask__sources">
               <span className="help-ask__sources-title">From the User Guide</span>
               <div className="help-center__articles">
                  {sources.map((article) => (
                     <ArticleLink key={article.id} article={article} onOpen={onOpenArticle} />
                  ))}
               </div>
            </div>
         )}
         {sources.length === 0 && footer}
      </div>
   );
};

/** Drawer view: the conversation, or suggested questions before it starts. */
export const AskAssistantView = ({
   assistant,
   articles,
   onOpenArticle,
   onClose,
   canOpenTickets
}) => {
   const { messages, ask, reset } = assistant;
   const endRef = useRef(null);
   const handleLinkClick = useGuideLinks({ articles, onOpen: onOpenArticle, onClose });
   const suggestions = useMemo(() => getSuggestions(articles), [articles]);

   useEffect(() => {
      endRef.current?.scrollIntoView?.({ block: "end", behavior: "smooth" });
   }, [messages]);

   const ticketLink = canOpenTickets && (
      <p className="help-ask__ticket">
         <Link to="/tickets" onClick={onClose}>
            Open a support ticket
         </Link>{" "}
         and our team will help.
      </p>
   );

   if (messages.length === 0) {
      return (
         <div className="help-ask help-ask--empty">
            <span className="help-center__empty-icon">
               <SparkleIcon size={28} />
            </span>
            <h4>Ask about KAPsurvey</h4>
            <p>
               Ask a question in your own words. The assistant answers from the KAPsurvey User
               Guide.
            </p>
            {suggestions.length > 0 && (
               <div className="help-ask__suggestions">
                  {suggestions.map((item) => (
                     <button
                        key={item.question}
                        type="button"
                        className="help-ask__suggestion"
                        onClick={() => ask(item.question)}
                     >
                        {item.question}
                     </button>
                  ))}
               </div>
            )}
         </div>
      );
   }

   return (
      <div className="help-ask">
         <div className="help-ask__toolbar">
            <Button type="link" size="small" onClick={reset}>
               New chat
            </Button>
         </div>

         {messages.map((message, index) => {
            if (message.role === "User") {
               return (
                  <div key={message.id} className="help-ask__message help-ask__message--user">
                     {message.content}
                  </div>
               );
            }

            if (message.status === "pending") {
               return (
                  <div
                     key={message.id}
                     className="help-ask__message help-ask__message--assistant help-ask__thinking"
                     aria-live="polite"
                  >
                     <span />
                     <span />
                     <span />
                     <span className="help-center__sr-only">Looking for an answer…</span>
                  </div>
               );
            }

            // The question this answers, so "Try again" asks it again as the
            // same turn rather than as a new one.
            const question = messages[index - 1];
            return (
               <AssistantMessage
                  key={message.id}
                  message={message}
                  articles={articles}
                  onLinkClick={handleLinkClick}
                  onOpenArticle={onOpenArticle}
                  onRetry={() => ask(question.content, { retryId: question.id })}
                  footer={ticketLink}
               />
            );
         })}
         <div ref={endRef} />
      </div>
   );
};

/** The question box, shown in the drawer's footer while Ask AI is open. */
export const AskComposer = ({ assistant }) => {
   const [draft, setDraft] = useState("");
   const canSend = draft.trim().length > 0 && !assistant.isPending;

   const send = () => {
      if (!canSend) return;
      assistant.ask(draft);
      setDraft("");
   };

   const handleKeyDown = (event) => {
      if (event.key !== "Enter" || event.shiftKey || event.nativeEvent?.isComposing) return;
      event.preventDefault();
      send();
   };

   return (
      <div className="help-ask__composer">
         <div className="help-ask__composer-box">
            <Input.TextArea
               value={draft}
               onChange={(event) => setDraft(event.target.value)}
               onKeyDown={handleKeyDown}
               placeholder="Ask a question…"
               aria-label="Your question"
               autoSize={{ minRows: 1, maxRows: 5 }}
               maxLength={MAX_QUESTION_LENGTH}
               bordered={false}
               autoFocus
            />
            <Button
               type="primary"
               shape="circle"
               icon={<ArrowUpOutlined />}
               onClick={send}
               disabled={!canSend}
               aria-label="Send"
            />
         </div>
         <div className="help-ask__composer-foot">
            <p className="help-ask__disclaimer">
               Answers come from KAPsurvey's help content and can be wrong. Check the linked
               articles.
            </p>
            {/* The backend refuses a longer question, so the box stops there. */}
            <span className="help-ask__count">
               {draft.length > 0 ? `${draft.length}/${MAX_QUESTION_LENGTH}` : ""}
            </span>
         </div>
      </div>
   );
};
